import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { Logger, LoggerFactory } from '@rillion/core/logger';
import { AppCrypto } from './app-crypto';

interface LocalData<T> {
  data: T;
}

@Injectable()
export class AppStorage {
  private logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private appCrypto: AppCrypto,
  ) {
    this.logger = loggerFactory.getLogger('AppStorage');
  }

  /* PUBLIC METHODS */

  saveData<T>(key: string, data: T) {
    const dataToPersist: LocalData<T> = {
      data: data,
    };
    this.setValue(key, JSON.stringify(dataToPersist));
  }

  loadDataSync<T>(key: string, defaultValue?: T): T | undefined {
    const data = this.getValueSync(key);
    if (data) {
      const json = JSON.parse(data) as LocalData<T>;
      return json.data;
    }
    return defaultValue;
  }

  loadData<T>(key: string, defaultValue?: T): Observable<T | undefined> {
    return from(
      this.getValue(key).then((data: string) => {
        if (data) {
          const json = JSON.parse(data) as LocalData<T>;
          return json.data;
        }
        return defaultValue;
      }),
    );
  }

  removeDataForKey(key: string) {
    this.logger.debug(`Removing key ${key}`);
    this.removeKey(key);
  }

  /* PRIVATE METHODS */

  private getValueSync(key: string): string {
    this.logger.debug(`Getting value for key ${key}`);

    // Encrypt key & value
    const encryptedKey = this.appCrypto.encrypt(key);
    const encryptedValFromStorage = localStorage.getItem(encryptedKey);
    const encryptedVal = encryptedValFromStorage == null ? '' : encryptedValFromStorage;
    return this.appCrypto.decrypt(encryptedVal);
  }

  private async getValue(key: string): Promise<any> {
    this.logger.debug(`Getting value for key ${key}`);

    // Encrypt key & value
    const encryptedKey = this.appCrypto.encrypt(key);
    const encryptedValFromStorage = localStorage.getItem(encryptedKey);
    const encryptedVal = encryptedValFromStorage == null ? '' : encryptedValFromStorage;
    return Promise.resolve(this.appCrypto.decrypt(encryptedVal));
  }

  private async setValue(key: string, value: string) {
    this.logger.debug(`Saving value for key ${key}`);
    // Encrypt key & value
    const encryptedKey = this.appCrypto.encrypt(key);
    const encryptedVal = this.appCrypto.encrypt(value);
    localStorage.setItem(encryptedKey, encryptedVal);
  }

  private async removeKey(key: string) {
    const encryptedKey = this.appCrypto.encrypt(key);
    localStorage.removeItem(encryptedKey);
  }

  /**
   * Removes all entries of this application from the local storage.
   */
  public clearStorage() {
    localStorage.clear();
    this.logger.debug('Local storage cleared');
  }
}
